import React, { useState, useEffect } from 'react';
import { businessMinutes, formatBusinessTime } from '../utils/businessHours';

const toHHMM = (t) => (t || '').slice(0, 5) || null;

export default function NBDTimer({
  faultReportedAt,
  frozenAt = null,
  isOnHold = false,
  holdStartedAt = null,
  totalHoldMinutes = 0,
  serviceStart = '09:00',
  serviceEnd = '17:00',
  resolved = false,
  warningMinutes = 20,
  breachMinutes = 30,
}) {
  serviceStart = toHHMM(serviceStart) || '09:00';
  serviceEnd = toHHMM(serviceEnd) || '17:00';
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!faultReportedAt) return;

    const calc = () => {
      const endTime = frozenAt ? new Date(frozenAt) : new Date();
      let raw = businessMinutes(new Date(faultReportedAt), endTime, serviceStart, serviceEnd);

      let holdMins = totalHoldMinutes;
      if (isOnHold && holdStartedAt) {
        const currentHold = businessMinutes(new Date(holdStartedAt), new Date(), serviceStart, serviceEnd);
        holdMins += currentHold;
      }

      setElapsed(Math.max(0, raw - holdMins));
    };

    calc();
    if (frozenAt) return;
    const id = setInterval(calc, 30000);
    return () => clearInterval(id);
  }, [faultReportedAt, frozenAt, isOnHold, holdStartedAt, totalHoldMinutes, serviceStart, serviceEnd]);

  if (!faultReportedAt) return null;

  const isBreached = elapsed >= breachMinutes;
  const isWarning  = !isBreached && elapsed >= warningMinutes;

  const severity = resolved  ? (isBreached ? 'breach_resolved' : 'ok_resolved')
    : isOnHold               ? 'hold'
    : isBreached             ? 'red'
    : isWarning              ? 'amber'
    :                          'green';

  const colors = {
    green:          { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', pulse: 'bg-emerald-400',  label: 'text-emerald-500'  },
    amber:          { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   pulse: 'bg-amber-400',    label: 'text-amber-500'    },
    red:            { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700',     pulse: 'bg-red-500',      label: 'text-red-500'      },
    hold:           { bg: 'bg-yellow-50',  border: 'border-yellow-300',  text: 'text-yellow-700',  pulse: 'bg-yellow-400',   label: 'text-yellow-600'   },
    breach_resolved:{ bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700',     pulse: 'bg-gray-300',     label: 'text-red-400'      },
    ok_resolved:    { bg: 'bg-gray-50',    border: 'border-gray-200',    text: 'text-gray-700',    pulse: 'bg-gray-300',     label: 'text-gray-400'     },
  }[severity];

  // What to display
  const breachedBy = elapsed - breachMinutes;
  const remaining  = breachMinutes - elapsed;

  let labelText, valueText, subText;

  if (resolved) {
    labelText = isBreached ? 'SLA Breached' : 'Resolved in';
    valueText = isBreached
      ? `+${formatBusinessTime(breachedBy)} over`
      : formatBusinessTime(elapsed);
    subText = `SLA target: ${breachMinutes}m`;
  } else if (isOnHold) {
    labelText = 'On Hold';
    valueText = formatBusinessTime(elapsed);
    subText   = `SLA target: ${breachMinutes}m`;
  } else if (isBreached) {
    labelText = 'SLA Breached';
    valueText = `+${formatBusinessTime(breachedBy)}`;
    subText   = `target was ${breachMinutes}m`;
  } else if (isWarning) {
    labelText = 'SLA Warning';
    valueText = `${formatBusinessTime(remaining)} left`;
    subText   = `${formatBusinessTime(elapsed)} elapsed`;
  } else {
    labelText = `SLA: ${breachMinutes}m`;
    valueText = formatBusinessTime(elapsed);
    subText   = `${formatBusinessTime(remaining)} remaining`;
  }

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${colors.bg} ${colors.border} min-w-[160px]`}>
      <div className="flex-shrink-0 relative w-3 h-3">
        {resolved ? (
          <span className={`block w-3 h-3 rounded-full ${colors.pulse}`} />
        ) : isOnHold ? (
          <span className={`block w-3 h-3 rounded-full ${colors.pulse}`} />
        ) : (
          <>
            <span className={`absolute inset-0 rounded-full ${colors.pulse} opacity-40 animate-ping`} />
            <span className={`absolute inset-0 rounded-full ${colors.pulse}`} />
          </>
        )}
      </div>
      <div className="min-w-0">
        <p className={`text-xs font-semibold uppercase tracking-wide ${colors.label}`}>{labelText}</p>
        <p className={`text-xl font-black leading-tight ${colors.text}`}>{valueText}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">{subText}</p>
        <p className="text-[10px] text-gray-300">{serviceStart}–{serviceEnd} Mon–Fri</p>
      </div>
    </div>
  );
}
